import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  X, AlertOctagon, HardHat, HeartPulse, FlaskConical, Stethoscope,
  Pill, ShieldAlert, Atom, Building2, BookOpen, Printer, FileDown,
} from "lucide-react";
import { CATEGORIES, SEVERITY_BG, SEVERITY_LABELS } from "../lib/diseases";
import { MEDICATIONS } from "../lib/medications";
import { buildDiseaseSheet } from "../lib/diseasePdf";

export const DiseaseDetailDialog = ({ disease, open, onOpenChange }) => {
  if (!disease) return null;
  const cat = CATEGORIES.find((c) => c.id === disease.cat);
  const sevCls = SEVERITY_BG[disease.severity] || "bg-zinc-300 text-black";
  const Icon = cat?.icon || Atom;

  const handlePrint = () => {
    document.body.classList.add("printing-disease");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-disease"), 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="disease-detail-dialog"
        className="max-w-5xl p-0 border-2 border-zinc-950 rounded-none bg-white max-h-[90vh] overflow-y-auto print:max-h-none print:overflow-visible disease-print-root"
      >
        <DialogTitle className="sr-only">{disease.name} — Occupational Disease</DialogTitle>
        <DialogDescription className="sr-only">
          {disease.aka || ""} — {cat?.title}. Severity: {SEVERITY_LABELS[disease.severity]}.
        </DialogDescription>

        {/* Header */}
        <div className="relative border-b-2 border-zinc-950">
          <div className="grid grid-cols-1 md:grid-cols-12">
            <div className={`md:col-span-4 ${cat?.tone || "bg-zinc-200"} border-r-0 md:border-r-2 border-zinc-950 p-8 flex flex-col justify-center items-center`}>
              <div className="font-mono text-xs uppercase tracking-[0.3em] mb-2">
                CAT · {cat?.n}
              </div>
              <div className="font-heading font-black text-7xl md:text-8xl leading-none tracking-tighter">
                {disease.code}
              </div>
              <div className="font-mono text-xs mt-3 uppercase tracking-widest text-center">
                {cat?.short}
              </div>
            </div>

            <div className="md:col-span-8 p-6 md:p-8 flex flex-col justify-between gap-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-1 flex items-center gap-1.5">
                    <Icon size={12} strokeWidth={2.5} /> Occupational Disease
                  </div>
                  <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase leading-none mb-1.5">
                    {disease.name}
                  </h2>
                  {disease.aka && (
                    <div className="font-mono text-xs text-zinc-600 italic">
                      {disease.aka}
                    </div>
                  )}
                </div>
                <button
                  data-testid="disease-detail-close"
                  onClick={() => onOpenChange(false)}
                  className="border-2 border-zinc-950 p-1.5 hover:bg-zinc-950 hover:text-white transition-colors print:hidden"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>

              <div className={`${sevCls} border-2 border-zinc-950 p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <AlertOctagon size={28} strokeWidth={2.75} />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Severity</div>
                    <div className="font-heading font-black text-2xl tracking-tight">
                      {SEVERITY_LABELS[disease.severity]}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest hidden sm:flex flex-col items-end gap-1">
                  <span>Category {cat?.n}</span>
                  <span className="opacity-80">{cat?.title}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cause */}
        <Section
          testid="disease-cause"
          icon={<FlaskConical strokeWidth={2.5} />}
          label="Cause"
          tone="bg-[#FFD700]"
        >
          {disease.cause}
        </Section>

        {/* What it is */}
        <Section
          testid="disease-what"
          icon={<BookOpen strokeWidth={2.5} />}
          label="What It Is"
          tone="bg-white"
        >
          {disease.what}
        </Section>

        {/* Forms (optional) */}
        {disease.forms && (
          <Section
            testid="disease-forms"
            icon={<Atom strokeWidth={2.5} />}
            label="Forms / Stages"
            tone="bg-zinc-50"
          >
            {disease.forms}
          </Section>
        )}

        {/* Symptoms */}
        <Section
          testid="disease-symptoms"
          icon={<HeartPulse strokeWidth={2.5} />}
          label="Symptoms"
          tone="bg-[#FF8A00]"
        >
          {disease.symptoms}
        </Section>

        {/* Diagnosis */}
        <Section
          testid="disease-diagnosis"
          icon={<Stethoscope strokeWidth={2.5} />}
          label="Diagnosis"
          tone="bg-[#00E5FF]"
        >
          {disease.diagnosis}
        </Section>

        {/* Treatment */}
        <Section
          testid="disease-treatment"
          icon={<Pill strokeWidth={2.5} />}
          label="Treatment"
          tone="bg-white"
        >
          {disease.treatment}
        </Section>

        {/* Prevention */}
        <Section
          testid="disease-prevention"
          icon={<HardHat strokeWidth={2.5} />}
          label="Prevention"
          tone="bg-[#FF003C] text-white"
          invert
        >
          {disease.prevention}
        </Section>

        {/* Pharmacology */}
        {MEDICATIONS[disease.id]?.length > 0 && (
          <div
            data-testid="disease-pharmacology"
            className="p-6 md:p-8 border-b-2 border-zinc-950 bg-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-[#00E5FF] font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
              <Pill size={14} strokeWidth={2.5} /> Pharmacology · Alphabetical
            </div>
            <div className="border-2 border-zinc-950 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-zinc-950 text-white font-mono uppercase text-[10px] tracking-[0.2em]">
                    <th className="px-3 py-2 border-r border-zinc-700 w-[220px]">Generic (Brand)</th>
                    <th className="px-3 py-2 border-r border-zinc-700 w-[200px]">Dose</th>
                    <th className="px-3 py-2">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {MEDICATIONS[disease.id].map((m, i) => (
                    <tr
                      key={m.name}
                      data-testid={`med-row-${disease.id}-${i}`}
                      className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}
                    >
                      <td className="px-3 py-2.5 border-t border-zinc-200 align-top">
                        <div className="font-heading font-bold text-sm tracking-tight">{m.name}</div>
                        {m.brand && m.brand !== "—" && (
                          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                            {m.brand}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 border-t border-zinc-200 align-top font-mono text-xs text-zinc-800">
                        {m.dose}
                      </td>
                      <td className="px-3 py-2.5 border-t border-zinc-200 align-top text-sm text-zinc-700 leading-snug">
                        {m.role}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 bg-[#FFD700] border-2 border-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-block">
              ⚠ Doses are reference only — confirm with current prescribing information & treating clinician.
            </div>
          </div>
        )}

        {/* Related Elements + Industries */}
        {(disease.elements?.length > 0 || disease.industries?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 border-b-2 border-zinc-950">
            {disease.elements?.length > 0 && (
              <div
                data-testid="disease-elements"
                className="p-6 md:p-8 md:border-r-2 md:border-zinc-950 bg-white"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-zinc-950 text-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
                  <Atom size={14} strokeWidth={2.5} /> Related Elements
                </div>
                <div className="flex flex-wrap gap-2">
                  {disease.elements.map((sym) => (
                    <Link
                      key={sym}
                      to={`/?el=${sym}`}
                      data-testid={`disease-element-link-${sym}`}
                      className="border-2 border-zinc-950 bg-zinc-50 hover:bg-[#FFD700] hover:neo-shadow hover:-translate-y-0.5 transition-all px-4 py-2 font-heading font-black text-2xl leading-none tracking-tighter inline-flex flex-col items-center gap-0.5"
                    >
                      <span>{sym}</span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] font-bold text-zinc-600">
                        view →
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {disease.industries?.length > 0 && (
              <div data-testid="disease-industries" className="p-6 md:p-8 bg-zinc-50">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
                  <Building2 size={14} strokeWidth={2.5} /> Affected Industries
                </div>
                <div className="flex flex-wrap gap-2">
                  {disease.industries.map((ind) => (
                    <span
                      key={ind}
                      className="bg-white border-2 border-zinc-950 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
                    >
                      {ind}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Optional note for the category */}
        {disease.note && (
          <div className="border-b-2 border-zinc-950 bg-[#FFD700] px-6 md:px-8 py-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1.5 flex items-center gap-1.5">
              <AlertOctagon size={12} strokeWidth={2.75} /> Note
            </div>
            <p className="text-sm text-zinc-950 leading-relaxed">{disease.note}</p>
          </div>
        )}

        {/* Footer */}
        <div className="bg-zinc-50 p-6 flex items-center justify-between flex-wrap gap-3 print:hidden">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-600">
            <ShieldAlert size={14} strokeWidth={2.5} />
            <span>Reference data · For training & education only</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => buildDiseaseSheet(disease)}
              data-testid="disease-detail-pdf"
              className="bg-[#FFD700] text-zinc-950 font-mono text-xs uppercase tracking-widest px-4 py-3 border-2 border-zinc-950 hover:neo-shadow hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
            >
              <FileDown size={14} strokeWidth={2.5} /> PDF
            </button>
            <button
              onClick={handlePrint}
              data-testid="disease-detail-print"
              className="bg-white text-zinc-950 font-mono text-xs uppercase tracking-widest px-4 py-3 border-2 border-zinc-950 hover:neo-shadow hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
            >
              <Printer size={14} strokeWidth={2.5} /> Print
            </button>
            <button
              onClick={() => onOpenChange(false)}
              className="bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-950 hover:bg-zinc-800 transition-colors"
              data-testid="disease-detail-close-bottom"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ icon, label, tone, children, invert, testid }) => (
  <div
    data-testid={testid}
    className={`p-6 md:p-8 border-b-2 border-zinc-950 ${invert ? tone : "bg-white"}`}
  >
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 ${
        invert ? "bg-white text-zinc-950" : tone
      } font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4`}
    >
      <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
      {label}
    </div>
    <p
      className={`text-base leading-relaxed ${
        invert ? "text-white font-medium" : "text-zinc-900"
      }`}
    >
      {children}
    </p>
  </div>
);
